// External libraries
import {
  genericValidationFn,
  getMaskedPhoneNumber,
  validatePhone,
} from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { logger } from '@/lib/logger';
import { getLocale } from '@/lib/globals';
import { features } from '@/lib/features';
import { redirectToGenericError } from '@/lib/utils/redirects';

const conditionallyUpdateShippingCountry = ({ country: merchantCountry }) => {
  const isDomesticShippingOnly = features.isActive('blockNonDomesticShipping');
  const buyerCountry = getLocale().country;
  const isNonDomesticTransaction =
    merchantCountry && merchantCountry !== buyerCountry;

  // If it is a domestic transaction or the merchant offers international
  // shipping then we do not need to update the shipping country
  if (!isNonDomesticTransaction || !isDomesticShippingOnly) {
    return;
  }

  logger.info('weasley_non_domestic_transaction_with_restricted_shipping', {
    buyerCountry,
    merchantCountry,
  });

  // Fetch address metadata for merchant country
  import('@/components/Address/api')
    .then(addressApi => {
      addressApi.fetchAddressMetadata(merchantCountry);
    })
    .catch(({ message }) => {
      logger.error('weasley_error_loading_address_api_file', { message });
      redirectToGenericError();
    });

  const { form: formActions } = appStore.getActions();
  formActions.updateShipToBilling(false);
  formActions.updateAddressField({
    addressType: 'shippingAddress',
    field: 'country',
    payload: { value: merchantCountry },
  });
};

/**
 * @function filterTerritories
 * @description Handles filtering logic for the data returned from the Griffin
 * metadata query. The filtering is currently only used to remove RU/TR from the
 * country dropdown list.
 */
const filterTerritories = territories => {
  if (!Array.isArray(territories)) {
    return territories;
  }
  const shouldExcludeGibraltar = features.isActive(
    'shouldDisableSignupForGibraltar',
  );

  return territories.filter(
    ({ code }) =>
      code !== 'RU' &&
      code !== 'TR' &&
      (shouldExcludeGibraltar ? code !== 'GI' : true),
  );
};

/**
 * @function getBillingNameFromCart
 * @param {object} payer
 * @param {object} shippingAddress
 * @returns {object} { firstName, lastName }
 */
const getBillingNameFromCart = ({ payer, shippingAddress }) => {
  const { familyName, givenName } = payer?.name || {};
  if (givenName || familyName) {
    return {
      firstName: givenName,
      lastName: familyName,
    };
  }

  const { firstName, isStoreAddress, lastName } = shippingAddress || {};
  // If the shipping address is a store address then we don't want to
  // pre-fill the billing name fields with the store name
  if (isStoreAddress) {
    return {};
  }

  return { firstName, lastName };
};

/**
 * @function getPhoneValidator
 * @param {object} phoneMeta { mask | pattern }
 * @returns {function}
 * @description Generates a function that takes a string value
 *  for a phone number and returns a validation object.
 */
const getPhoneValidator = ({ mask, pattern }) => {
  return value => {
    const props = {
      maxLength: mask?.length,
      minLength: mask?.length,
      pattern,
      required: true,
    };
    const maskedValue = getMaskedPhoneNumber(value, mask);
    return validatePhone(value, { maskedValue, props });
  };
};

/**
 * @function validateEmail
 * @param {string} value
 * @returns {object}
 */
const validateEmail = value => {
  const props = { required: true, type: 'email' };
  return genericValidationFn(value, { props });
};

export {
  conditionallyUpdateShippingCountry,
  filterTerritories,
  getBillingNameFromCart,
  getPhoneValidator,
  validateEmail,
};
